import {
  Box,
  HStack,
  Text,
  useColorModeValue,
  useToken,
} from "@chakra-ui/react";
import clamp from "lodash/clamp";
import { useMemo } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  IncompleteBarPattern,
  incompleteBarPatternId,
} from "@/common/charts/components/IncompleteBar";
import useChartTokens, {
  useChartColorTokens,
} from "@/common/charts/hooks/useChartTokens";
import { epochIsSame, epochToShortDate } from "@/common/utils/datetime";
import { formatNumber } from "@/common/utils/numberFormatting";
import {
  FeedbackSatisfactionIcon,
  useSatisfactionColors,
} from "@/feedback/components/FeedbackSatisfaction";
import {
  SATISFACTION_LABELS,
  SATISFACTION_LABELS_SHORT,
} from "@reflag/shared/constants";
import { FeedbackTimelineItem } from "@reflag/shared/feedbackAPI";
import { SatisfactionScore } from "@reflag/shared/schemas/satisfactionScore";

export type SatisfactionAverageTimelineChartDataKeyType = {
  key: string;
  name: string;
  type: "bar" | "line";
  color: keyof useChartColorTokens;
  style?: "solid" | "dashed";
};

export type SatisfactionAverageTimelineChartProps = {
  data: FeedbackTimelineItem[];
  isLoading?: boolean;
  height?: number;
  highlightLatest?: boolean;
  highlightPartialPeriod?: boolean;
  axisLabelFontSize?: string;
};

export default function SatisfactionAverageTimelineChart({
  data = [],
  isLoading = false,
  height = 300,
  highlightPartialPeriod = false,
  highlightLatest = false,
  axisLabelFontSize,
}: SatisfactionAverageTimelineChartProps) {
  const { colors, fontSizes, radii, sizes, properties } = useChartTokens();
  const axisLabelFontSizeValue = axisLabelFontSize || fontSizes.axisLabel;
  const lastDataPeriod = useMemo<[number, number]>(
    () =>
      data
        .filter((point) => "total" in point)
        .map(({ epoch }) => epoch)
        .reverse()
        .slice(0, 2) as [number, number],
    [data],
  );

  const lineColor = useToken(
    "colors",
    useColorModeValue("brand.400", "brand.400"),
  );

  return (
    <ResponsiveContainer height={height} width="100%">
      <LineChart
        data={data}
        margin={{
          top: 30,
          right: 0,
          bottom: 0,
          left: 0,
        }}
      >
        <CartesianGrid
          stroke={colors.grid}
          strokeDasharray="3"
          vertical={false}
          yAxisId="line"
        />
        <XAxis
          axisLine={true}
          dataKey="epoch"
          domain={["dataMin", "dataMax"]}
          fontSize={axisLabelFontSizeValue}
          height={20}
          interval="preserveEnd"
          minTickGap={4}
          scale="utc"
          stroke={colors.axis}
          tickFormatter={(epoch: number, _index: number) => {
            if (epochIsSame(1712527200, "year")) return "Now";
            return epochToShortDate(epoch);
          }}
          tickLine={true}
          type="number"
        ></XAxis>
        <YAxis
          axisLine={false}
          dataKey="averageScore"
          domain={[1, 5]}
          fontSize={axisLabelFontSizeValue}
          mirror={false}
          stroke={colors.axis}
          tick={{
            style: {
              fill: colors.axis,
              paintOrder: "stroke",
              strokeLinejoin: "round",
            },
          }}
          tickFormatter={(value: SatisfactionScore) =>
            SATISFACTION_LABELS_SHORT[value]
          }
          tickLine={false}
          ticks={[1, 2, 3, 4, 5]}
          type="number"
          width={90}
          yAxisId="line"
        />
        {highlightPartialPeriod && data?.length && (
          <>
            <defs>
              <IncompleteBarPattern
                colorName={"partialHighlight"}
                colorValue={colors.partialHighlight}
              />
            </defs>
            <ReferenceArea
              fill={`url(#${incompleteBarPatternId("partialHighlight")})`}
              x1={lastDataPeriod[0]}
              x2={lastDataPeriod[1]}
              yAxisId="line"
            />
          </>
        )}
        {highlightLatest && data?.length && (
          <ReferenceArea
            fill={colors.highlightBg}
            fillOpacity={0.4}
            radius={10}
            x1={lastDataPeriod[0]}
            x2={lastDataPeriod[1]}
            yAxisId="line"
          />
        )}
        <Tooltip
          contentStyle={{
            borderColor: colors.tooltipBorder,
            backgroundColor: colors.tooltipBg,
            borderRadius: radii.tooltip,
            fontSize: fontSizes.tooltip,
          }}
          cursor={{ stroke: colors.cursor }}
          formatter={(value) => {
            if (typeof value === "number") {
              const score = clamp(Math.round(value), 1, 5) as SatisfactionScore;
              return (
                <Box
                  alignItems="center"
                  as="span"
                  display="inline-flex"
                  gap={1}
                  verticalAlign="bottom"
                >
                  <FeedbackSatisfactionIcon
                    size="xl"
                    value={score}
                    verticalAlign={"bottom"}
                    colored
                  />
                  <Text>{SATISFACTION_LABELS[score]}</Text>
                  <Text color="dimmed">({formatNumber(value)})</Text>
                </Box>
              ) as unknown as string;
            }

            return value;
          }}
          isAnimationActive={false}
          labelFormatter={epochToShortDate}
        />
        {!isLoading && (
          <Line
            activeDot={properties.activeDot}
            dataKey={"averageScore"}
            dot={false}
            isAnimationActive={false}
            name={"Score"}
            stroke={lineColor}
            strokeLinecap="round"
            strokeWidth={sizes.lineStrokeWidth}
            type="monotone"
            yAxisId="line"
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
}

export function SatisfactionAverageTimelineChartLegend() {
  return (
    <HStack spacing={2}>
      <Box
        borderColor={useToken("colors", useSatisfactionColors(3))}
        borderLeftWidth={4}
        borderRadius="full"
        h={4}
        ml={1}
        transform="rotate(45deg)"
        w={0}
      />
      <Text color="dimmed" fontSize="sm">
        Average satisfaction
      </Text>
    </HStack>
  );
}
